/**
 * Pure helpers for the images a user attaches in the feedback dialog
 * (pasted with Strg+V, dropped or picked — App-Feedback 2026-08-04: „habe
 * einen Screenshot gemacht, weiß aber nicht, wie ich ihn hier einfügen
 * kann“). Kept free of DOM/React so `npm test` (node --test) covers them;
 * the canvas work lives in `prepare_feedback_image.ts`.
 */

/** Max number of own images per feedback — mirrors the backend DTO (ArrayMaxSize 5). */
export const FEEDBACK_MAX_ATTACHMENTS = 5;

/** Long edge (px) images are downscaled to before upload. */
export const FEEDBACK_ATTACHMENT_MAX_EDGE_PX = 2560;

/** JPEG quality used when re-encoding attached images. */
export const FEEDBACK_ATTACHMENT_JPEG_QUALITY = 0.85;

/**
 * PNGs up to this size (and within the edge limit) are sent unchanged: UI
 * screenshots are mostly text, which stays sharp only lossless. Everything
 * else — photos, huge screenshots — is re-encoded as JPEG.
 */
export const FEEDBACK_PNG_PASSTHROUGH_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Upper bound for the summed size of all own images after re-encoding:
 * 10 MB ≈ 13.3 MB as base64 (≤ 15 MB encoded). Keeps the request small and
 * the notification mail below the mail provider's size limit next to the
 * page screenshot. Counted in decoded bytes so the limit matches the size
 * the dialog shows.
 */
export const FEEDBACK_ATTACHMENTS_MAX_TOTAL_BYTES = 10 * 1024 * 1024;

/** Image types the browser can decode for re-encoding. */
export function isSupportedImageType(type: string | undefined | null): boolean {
  return typeof type === "string" && /^image\//i.test(type);
}

/**
 * Target size for downscaling: the long edge is capped at `maxEdge`, the
 * aspect ratio is kept, images that are already small stay untouched.
 */
export function computeDownscaledSize(
  width: number,
  height: number,
  maxEdge: number = FEEDBACK_ATTACHMENT_MAX_EDGE_PX,
): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 1, height: 1 };
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width: Math.round(width), height: Math.round(height) };
  const factor = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
  };
}

/** Raw base64 payload of a `data:…;base64,` URL (everything after the comma). */
export function dataUrlBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : "";
}

/** Decoded byte size of a raw (padded) base64 string. */
export function base64DecodedLength(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, (base64.length / 4) * 3 - padding);
}

/** „1,4 MB“ / „380 KB“ — German size label for the dialog. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("de-DE")} KB`;
  }
  return `${(bytes / (1024 * 1024)).toLocaleString("de-DE", {
    maximumFractionDigits: 1,
  })} MB`;
}

/**
 * Whether an attached image is sent as-is instead of being re-encoded: only
 * a decodable PNG that is already small enough in pixels and bytes.
 */
export function shouldKeepOriginalImage(image: {
  type: string;
  byteSize: number;
  width: number;
  height: number;
}): boolean {
  return (
    image.type.toLowerCase() === "image/png" &&
    image.byteSize > 0 &&
    image.byteSize <= FEEDBACK_PNG_PASSTHROUGH_MAX_BYTES &&
    image.width > 0 &&
    image.height > 0 &&
    Math.max(image.width, image.height) <= FEEDBACK_ATTACHMENT_MAX_EDGE_PX
  );
}

/**
 * File name of the attachment as sent: the original name with the extension
 * of the content type actually sent (a re-encoded image would otherwise
 * keep a lying `.png`).
 */
export function attachmentFileName(
  originalName: string | undefined | null,
  contentType: "image/jpeg" | "image/png",
): string {
  const trimmed = (originalName ?? "").trim();
  const base = trimmed.replace(/\.[^./\\]*$/, "");
  return `${base || "bild"}.${contentType === "image/png" ? "png" : "jpg"}`;
}

/** Minimal shape of a clipboard file we need (a `File`). */
export interface ClipboardFileLike {
  type: string;
}

/** Minimal shape of a `DataTransferItem`. */
export interface ClipboardItemLike<F extends ClipboardFileLike> {
  kind: string;
  type: string;
  getAsFile(): F | null;
}

/**
 * Image files of a paste event's `clipboardData`. Browsers expose a pasted
 * screenshot via `files` and/or `items` (Chrome: both, carrying the same
 * image) — prefer `files` and only fall back to `items` when it is empty, so
 * one screenshot is never attached twice.
 */
export function pickClipboardImageFiles<F extends ClipboardFileLike>(data: {
  files?: ArrayLike<F> | null;
  items?: ArrayLike<ClipboardItemLike<F>> | null;
}): F[] {
  const fromFiles = Array.from(data.files ?? []).filter((f) => isSupportedImageType(f.type));
  if (fromFiles.length) return fromFiles;
  const fromItems: F[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind !== "file" || !isSupportedImageType(item.type)) continue;
    const file = item.getAsFile();
    if (file) fromItems.push(file);
  }
  return fromItems;
}

/**
 * Whether a paste should become an attachment. A paste of text into the
 * message field must keep working even if the clipboard additionally holds
 * an image (e.g. cells copied from Excel carry text AND a picture) — only a
 * paste without text, or one outside a text field, attaches the image.
 */
export function shouldAttachPastedImages(params: {
  imageCount: number;
  clipboardHasText: boolean;
  targetIsTextField: boolean;
}): boolean {
  if (params.imageCount <= 0) return false;
  return !(params.targetIsTextField && params.clipboardHasText);
}

/**
 * How many of `incoming` new images still fit next to the `current` ones
 * (incl. those still being processed).
 */
export function planAttachmentIntake(
  current: number,
  incoming: number,
  max: number = FEEDBACK_MAX_ATTACHMENTS,
): { accepted: number; rejected: number } {
  const free = Math.max(0, max - current);
  const accepted = Math.min(free, Math.max(0, incoming));
  return { accepted, rejected: Math.max(0, incoming) - accepted };
}

/** Whether one more image of `addBytes` still fits the total budget. */
export function fitsAttachmentBudget(
  currentBytes: readonly number[],
  addBytes: number,
  budget: number = FEEDBACK_ATTACHMENTS_MAX_TOTAL_BYTES,
): boolean {
  const used = currentBytes.reduce((sum, n) => sum + n, 0);
  return used + addBytes <= budget;
}
